import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useEffect, useState } from 'react';
import { VideoFeatureList } from '@/components/feature-list';
import { VideoFeatures } from '@/store/useVideoFeatures';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSidebarPreferences } from '@/store/sidebarPreferences';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { VideoSelectorCompact } from './VideoSelectorCompact';
import { ImportConfigurationsModal } from './ImportConfigurationsModal';
import { HelpCircle, Check, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';

interface FeatureListSectionProps {
  features: VideoFeatures;
  onFeatureClick: (featureId: string) => void;
  activeEditingPanel?: string | null;
  currentVideo?: {
    id: string;
    title: string;
    thumbnailUrl: string;
  } | null;
  onChangeVideo?: () => void;
}

export const FeatureListSection: React.FC<FeatureListSectionProps> = ({ 
  features, 
  onFeatureClick,
  activeEditingPanel,
  currentVideo,
  onChangeVideo
}) => {
  useLanguage();
  // Early return if features is undefined
  if (!features) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        {translateInterfaceText("Carregando recursos...")}</div>
    );
  }

  const navigate = useNavigate();
  const location = useLocation();
  const isPersonalizationRoute = location.pathname.startsWith('/personalizations');
  const breadcrumbText = isPersonalizationRoute ? translateInterfaceText("Configurações do Vídeo") : 'Novo Vídeo';
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>(() => {
    const saved = localStorage.getItem('recently-used-features');
    return saved ? JSON.parse(saved) : [];
  });
  const [importModalOpen, setImportModalOpen] = useState(false);
  
  
  // PRD 3.5: Estado para feedback do botão "Aplicar como padrão"
  const [syncSuccess, setSyncSuccess] = useState(false);
  
  // State management for hover and selection - synchronized with activeEditingPanel
  const [selectedId, setSelectedId] = useState<string | null>(activeEditingPanel);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Sync selectedId with activeEditingPanel
  useEffect(() => {
    setSelectedId(activeEditingPanel);
  }, [activeEditingPanel]);

  // Enhanced feature click handler
  const handleFeatureClick = (featureId: string) => {
    // Add the feature to the beginning of the recently used list
    // and remove duplicates
    const updatedRecent = [featureId, ...recentlyUsed.filter(id => id !== featureId)].slice(0, 5);
    setRecentlyUsed(updatedRecent);
    localStorage.setItem('recently-used-features', JSON.stringify(updatedRecent));
    
    // Update selection state
    setSelectedId(featureId);
    
    // Call the original onFeatureClick
    onFeatureClick(featureId);
  };

  // Handle item hover 
  const handleItemHover = (itemId: string | null) => {
    setHoveredId(itemId);
  };

  const handleBack = () => {
    navigate('/dashboard');
  };
  
  // PRD 3.5: Handler para sucesso da sincronização
  const handleSyncComplete = () => {
    setImportModalOpen(false);
    setSyncSuccess(true);
    toast.success(translateInterfaceText("Configuração aplicada com sucesso"));
    setTimeout(() => setSyncSuccess(false), 2500);
  };

  // Mark that user has visited New Video page (for default open state)
  useEffect(() => {
    localStorage.setItem('has-visited-new-video', 'true');
  }, []);

  return (
    <div className="h-full py-2 pl-4 pr-6 flex flex-col" data-features-sidebar>
      {/* Breadcrumb de navegação (estilo Teste A/B) */}
      <div className="pt-3 pb-2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink 
                onClick={handleBack}
                className="cursor-pointer"
              >
                {translateInterfaceText("Voltar")}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{breadcrumbText}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Seletor de Vídeo Compacto - Apenas em /personalizations */}
      {isPersonalizationRoute && currentVideo && onChangeVideo && (
        <div className="py-2 mb-2">
          <VideoSelectorCompact 
            currentVideo={currentVideo}
            onChangeVideo={onChangeVideo}
          />
        </div>
      )}

      {/* Botão "Aplicar como padrão" */}
      <div className="flex items-center gap-2 py-2 mb-2">

        {/* PRD 3.5: Botão "Aplicar como padrão" refinado com feedback */}
        <button
          onClick={() => setImportModalOpen(true)}
          disabled={syncSuccess}
          className={cn(
            "inline-flex items-center gap-2 h-9 px-4 rounded-full font-medium text-[13px] transition-all duration-200 border group w-fit",
            syncSuccess 
              ? "bg-[hsl(222,91%,97%)] border-[hsl(222,91%,80%)] text-[hsl(222,91%,56%)] cursor-default"
              : "hover:bg-[rgba(45,108,246,0.13)] hover:border-[rgba(45,108,246,0.4)]"
          )}
          style={!syncSuccess ? {
            background: 'rgba(45, 108, 246, 0.06)',
            borderColor: 'rgba(45, 108, 246, 0.25)',
            color: '#6B7280'
          } : undefined}
        >
          {syncSuccess ? (
            <>
              <Check className="w-4 h-4" strokeWidth={2.5} />
              {translateInterfaceText("Aplicado")}</>
          ) : (
            <>
              <svg 
                className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180 text-[#2D6CF6]" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                strokeWidth="2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {translateInterfaceText("Aplicar como padrão")}</>
          )}
        </button>

        {/* Tooltip informativo ao lado direito */}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="inline-flex items-center justify-center w-5 h-5 rounded-full text-muted-foreground hover:text-primary transition-colors">
                <Info className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="w-[160px] text-xs leading-relaxed p-3 mt-3">
              {translateInterfaceText("Use este vídeo como modelo para aplicar as mesmas configurações nos outros vídeos.")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto mt-2 scrollbar-thin scrollbar-track-[#E5E7EB] scrollbar-thumb-[#316BFF] scrollbar-thumb-rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-[#316BFF]"
      >
        <VideoFeatureList
          features={features}
          onFeatureClick={handleFeatureClick}
          className="animate-fade-in"
          isOrganizeMode={false}
          selectedId={selectedId}
          hoveredId={hoveredId}
          onItemHover={handleItemHover}
        />
      </div>

      {/* Modal Importar Configurações - com callback de sucesso */}
      <ImportConfigurationsModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        currentVideoId={currentVideo?.id}
        currentVideo={currentVideo}
        onSuccess={handleSyncComplete}
      />

    </div>
  );
};
